/* Build & Dev. By Hadasi Media
 * hm-carousel - reusable infinite-loop + autoplay carousel engine.
 * Any section opts in with markup:
 *   <div data-hm-carousel data-autoplay="true" data-interval="4500" data-loop="true">
 *     <div data-hm-carousel-viewport>
 *       <div data-hm-carousel-track>
 *         <div data-hm-carousel-slide>...</div> ...
 *       </div>
 *     </div>
 *     <button data-hm-carousel-prev>..</button>
 *     <button data-hm-carousel-next>..</button>
 *     <div data-hm-carousel-dots></div>   (optional - dots auto-generated)
 *   </div>
 * Centered: the active slide is centered, neighbours peek. Pixel-offset math
 * keeps it correct in both LTR and RTL. Infinite via a 3x cloned slide set.
 */
(function () {
  'use strict';

  function HMCarousel(root) {
    this.root = root;
    this.viewport = root.querySelector('[data-hm-carousel-viewport]');
    this.track = root.querySelector('[data-hm-carousel-track]');
    if (!this.viewport || !this.track) return;

    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.loop = root.dataset.loop !== 'false';
    this.autoplay = root.dataset.autoplay === 'true' && !this.reduced;
    this.interval = parseInt(root.dataset.interval || '4500', 10);
    this.transMs = parseInt(root.dataset.transition || '500', 10);

    this.reals = Array.prototype.slice.call(this.track.querySelectorAll('[data-hm-carousel-slide]'));
    this.n = this.reals.length;
    if (this.n === 0) return;

    this.abs = 0;          // absolute index across cloned sets
    this.centers = [];     // pixel center of every (cloned) slide within the track
    this.timer = null;
    this.dragging = false;

    if (this.loop && this.n > 1) this._buildClones();
    this.slides = Array.prototype.slice.call(this.track.querySelectorAll('[data-hm-carousel-slide]'));
    this.abs = (this.loop && this.n > 1) ? this.n : 0; // start on first real slide

    this._buildDots();
    this._bind();
    this._measure();
    this._goImmediate(this.abs);
    if (this.autoplay && this.n > 1) this._start();

    // Recompute on resize / media load
    var self = this;
    if ('ResizeObserver' in window) {
      this._ro = new ResizeObserver(function () { self._measure(); self._goImmediate(self.abs); });
      this._ro.observe(this.viewport);
    } else {
      window.addEventListener('resize', function () { self._measure(); self._goImmediate(self.abs); });
    }
    var remeasure = function () { if (!self.dragging) { self._measure(); self._goImmediate(self.abs); } };
    this.track.querySelectorAll('img').forEach(function (m) { m.addEventListener('load', remeasure); });
    this.track.querySelectorAll('video').forEach(function (m) { m.addEventListener('loadedmetadata', remeasure); });
  }

  HMCarousel.prototype._buildClones = function () {
    var before = document.createDocumentFragment();
    var after = document.createDocumentFragment();
    var prep = function (c) {
      c.setAttribute('aria-hidden', 'true');
      c.dataset.clone = '1';
      // Keep clones out of the tab order / AT tree (aria-hidden alone does not unfocus)
      try { c.inert = true; } catch (e) {}
      c.querySelectorAll('a, button, video, [tabindex]').forEach(function (el) { el.setAttribute('tabindex', '-1'); });
    };
    for (var i = 0; i < this.n; i++) {
      var c1 = this.reals[i].cloneNode(true); prep(c1);
      var c2 = this.reals[i].cloneNode(true); prep(c2);
      before.appendChild(c1);
      after.appendChild(c2);
    }
    this.track.insertBefore(before, this.reals[0]);
    this.track.appendChild(after);
  };

  HMCarousel.prototype._buildDots = function () {
    this.dotsWrap = this.root.querySelector('[data-hm-carousel-dots]');
    this.dots = [];
    if (!this.dotsWrap || this.n < 2) return;
    this.dotsWrap.innerHTML = '';
    for (var i = 0; i < this.n; i++) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'hm-carousel__dot';
      b.setAttribute('aria-label', 'מעבר לכרטיס ' + (i + 1));
      b.dataset.idx = i;
      this.dotsWrap.appendChild(b);
      this.dots.push(b);
    }
  };

  HMCarousel.prototype._measure = function () {
    this.centers = this.slides.map((function (s) {
      return s.offsetLeft + s.offsetWidth / 2;
    }));
    this.vpCenter = this.viewport.clientWidth / 2;
  };

  HMCarousel.prototype._translateFor = function (absIndex) {
    if (!this.centers.length) return 0;
    var idx = Math.max(0, Math.min(absIndex, this.centers.length - 1));
    return -(this.centers[idx] - this.vpCenter);
  };

  HMCarousel.prototype._apply = function (x, animate) {
    this.track.style.transition = animate ? ('transform ' + this.transMs + 'ms cubic-bezier(0.25,1,0.5,1)') : 'none';
    this.track.style.transform = 'translateX(' + x + 'px)';
  };

  HMCarousel.prototype._goImmediate = function (absIndex) {
    this.abs = absIndex;
    this._apply(this._translateFor(absIndex), false);
    this._syncActive();
  };

  HMCarousel.prototype._go = function (absIndex) {
    var self = this;
    this.abs = absIndex;
    this._apply(this._translateFor(absIndex), true);
    this._syncActive();
    if (!this.loop || this.n < 2) return;
    var onEnd = function (e) {
      // Only react to the track's own transform finishing - not child opacity/transform
      // transitions (slide scale/opacity) that bubble up to the track.
      if (e.target !== self.track || e.propertyName !== 'transform') return;
      self.track.removeEventListener('transitionend', onEnd);
      var real = self.abs;
      if (self.abs < self.n) real = self.abs + self.n;
      else if (self.abs >= self.n * 2) real = self.abs - self.n;
      if (real !== self.abs) self._goImmediate(real);
    };
    this.track.addEventListener('transitionend', onEnd);
  };

  HMCarousel.prototype.next = function () { this._go(this.abs + 1); };
  HMCarousel.prototype.prev = function () { this._go(this.abs - 1); };

  HMCarousel.prototype.toReal = function (realIdx) {
    var base = (this.loop && this.n > 1) ? this.n : 0;
    this._go(base + realIdx);
  };

  HMCarousel.prototype._realIndex = function () {
    if (!(this.loop && this.n > 1)) return ((this.abs % this.n) + this.n) % this.n;
    return (((this.abs - this.n) % this.n) + this.n) % this.n;
  };

  HMCarousel.prototype._syncActive = function () {
    var real = this._realIndex();
    this.slides.forEach((function (s, i) {
      var isC = (i === this.abs);
      s.classList.toggle('is-center', isC);
    }).bind(this));
    if (this.dots.length) {
      this.dots.forEach(function (d, i) {
        d.classList.toggle('is-active', i === real);
        if (i === real) d.setAttribute('aria-current', 'true');
        else d.removeAttribute('aria-current');
      });
    }
    // Play centered video, pause the rest
    this.slides.forEach((function (s, i) {
      var v = s.querySelector('video');
      if (!v) return;
      if (i === this.abs && !this.reduced) { var p = v.play(); if (p && p.catch) p.catch(function () {}); }
      else { v.pause(); }
    }).bind(this));
  };

  HMCarousel.prototype._start = function () {
    var self = this;
    this._stop();
    this.timer = window.setInterval(function () { if (!self.dragging) self.next(); }, this.interval);
  };
  HMCarousel.prototype._stop = function () { if (this.timer) { clearInterval(this.timer); this.timer = null; } };

  HMCarousel.prototype._bind = function () {
    var self = this;
    var prev = this.root.querySelector('[data-hm-carousel-prev]');
    var next = this.root.querySelector('[data-hm-carousel-next]');
    // RTL: prev arrow (visually right) shows the previous slide
    if (prev) prev.addEventListener('click', function () { self.prev(); self._restart(); });
    if (next) next.addEventListener('click', function () { self.next(); self._restart(); });

    if (this.dotsWrap) {
      this.dotsWrap.addEventListener('click', function (e) {
        var b = e.target.closest('[data-idx]');
        if (!b) return;
        self.toReal(parseInt(b.dataset.idx, 10));
        self._restart();
      });
    }

    // Pause on hover / when tab hidden
    this.root.addEventListener('mouseenter', function () { self._stop(); });
    this.root.addEventListener('mouseleave', function () { if (self.autoplay && self.n > 1) self._start(); });
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) self._stop();
      else if (self.autoplay && self.n > 1) self._start();
    });

    // Keyboard
    this.viewport.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); self.prev(); self._restart(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); self.next(); self._restart(); }
    });

    // Pointer drag / swipe (live follow). RTL-safe: on release we snap to the
    // slide whose center is nearest the current dragged position, so the snap
    // direction always matches the drag - no sign flips, and it always "locks"
    // onto a card. Horizontal swipes are captured (preventDefault) so the page
    // doesn't scroll away mid-swipe on touch.
    var startX = 0, startY = 0, baseX = 0, moved = 0, horiz = false;
    var onDown = function (e) {
      self.dragging = true; moved = 0; horiz = false;
      var pt = e.touches ? e.touches[0] : e;
      startX = pt.clientX; startY = pt.clientY;
      baseX = self._translateFor(self.abs);
      self.track.style.transition = 'none';
      self._stop();
    };
    var onMove = function (e) {
      if (!self.dragging) return;
      var pt = e.touches ? e.touches[0] : e;
      var dx = pt.clientX - startX;
      var dy = pt.clientY - startY;
      if (!horiz) {
        if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 6) horiz = true;
        else if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 10) { self.dragging = false; return; }
        else return;
      }
      moved = dx;
      if (e.cancelable) e.preventDefault(); // lock the horizontal swipe to the carousel
      self.track.style.transform = 'translateX(' + (baseX + moved) + 'px)';
    };
    var onUp = function () {
      if (!self.dragging) return;
      self.dragging = false;
      // Suppress the click that follows a real drag so linked cards don't navigate on swipe
      self._suppressClick = Math.abs(moved) > 5;
      var current = baseX + moved;               // where the track physically sits now
      var best = self.abs, bestD = Infinity;
      var lo = Math.max(0, self.abs - 2);
      var hi = Math.min(self.centers.length - 1, self.abs + 2);
      for (var k = lo; k <= hi; k++) {
        var d = Math.abs(self._translateFor(k) - current);
        if (d < bestD) { bestD = d; best = k; }
      }
      self._go(best);                            // snap to nearest = always locks, direction-correct
      if (self.autoplay && self.n > 1) self._start();
    };
    this.viewport.addEventListener('mousedown', onDown);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    this.viewport.addEventListener('touchstart', onDown, { passive: true });
    this.viewport.addEventListener('touchmove', onMove, { passive: false });
    this.viewport.addEventListener('touchend', onUp);
    this.viewport.addEventListener('touchcancel', onUp);
    // Block the post-drag click on linked cards (capture phase, before the anchor sees it)
    this.viewport.addEventListener('click', function (e) {
      if (self._suppressClick) { e.preventDefault(); e.stopPropagation(); self._suppressClick = false; }
    }, true);
  };

  HMCarousel.prototype._restart = function () {
    if (this.autoplay && this.n > 1) this._start();
  };

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-hm-carousel]').forEach(function (el) {
      if (el.__hmCarousel) return;
      el.__hmCarousel = new HMCarousel(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }
  // Shopify theme editor: re-init on section load
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });

  window.HMCarousel = HMCarousel;
  window.hmCarouselInit = initAll;
})();
